<script setup>
import navItems from '@/navigation/vertical'
import navCustomerItems from '@/navigation/customer'
import navSuperItems from '@/navigation/super'
import { themeConfig } from '@themeConfig'

// Components
import Footer from '@/layouts/components/Footer.vue'
import NavbarThemeSwitcher from '@/layouts/components/NavbarThemeSwitcher.vue'
import UserProfile from '@/layouts/components/UserProfile.vue'
import NavBarI18n from '@core/components/I18n.vue'

// @layouts plugin
import { VerticalNavLayout } from '@layouts'

const userStore = useUserStore()

// MUST be computed, not a plain const. The user store is persisted with
// pinia-plugin-persistedstate, so `profile` starts as null and is hydrated a tick
// later. Evaluating this once at setup meant that whenever the layout mounted
// before hydration finished — typically straight after a login redirect or a full
// page load — the chain fell through to [] and, being non-reactive, the sidebar
// stayed permanently empty until a lucky reload. That is the "my whole navigation
// disappeared" report.
const navItemsForUser = computed(() => {
  const profile = userStore.profile
  if (!profile) return []
  if (profile.role === 'Super') return navSuperItems
  if (profile.acl === 'Customer') return navCustomerItems
  if (profile.acl === 'Company') return navItems

  return []
})

// Wave D: items tagged `experience: 'hospitality'` disappear when every
// business this user can see is retail (a shop has no floor plans). Mixed
// accounts and the not-yet-loaded state keep the full nav (fail open).
const { isAllRetail } = useAccountExperience()

// either everyone can access it or user role have the access
const navItemsFiltered = computed(() =>
  navItemsForUser.value.filter(
    item =>
      (item.acl?.includes('*') || item.acl?.includes(userStore.profile?.role))
      && !(item.experience === 'hospitality' && isAllRetail.value)
  )
)
</script>

<template>
  <VerticalNavLayout :nav-items="navItemsFiltered">
    <!-- 👉 navbar -->
    <template #navbar="{ toggleVerticalOverlayNavActive }">
      <div class="d-flex h-100 align-center">
        <VBtn
          id="vertical-nav-toggle-btn"
          class="ms-n3 d-lg-none"
          @click="toggleVerticalOverlayNavActive(true)"
        >
          <VIcon
            icon="tabler-menu-2"
            size="26"
          />
        </VBtn>

        <VSpacer/>
        <NavbarThemeSwitcher/>

        <ClientOnly>
          <NavBarI18n
            v-if="themeConfig.app.i18n.enable && themeConfig.app.i18n.langConfig?.length"
            :languages="themeConfig.app.i18n.langConfig"
          />
          <UserProfile/>
        </ClientOnly>
      </div>
    </template>

    <!-- 👉 Pages -->
    <slot/>

    <!-- 👉 Footer -->
    <template #footer>
      <Footer/>
    </template>

    <!-- 👉 Customizer -->
    <!-- <TheCustomizer /> -->
  </VerticalNavLayout>
</template>
